import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Save, X, 
  Lightbulb, Eye, EyeOff, ListOrdered, StickyNote, 
  Sparkles, Brain, Loader2, ToggleLeft, ToggleRight,
  RefreshCw, History, RotateCcw,
  GripVertical, Plus, FileUp, Layers, MoreHorizontal, Copy, Trash2, Pencil,
  FileText, Archive, Printer
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import RemixMenu from "@/components/RemixMenu";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ============ AUTO-RESIZE TEXTAREA ============
const AutoResizeTextarea = ({ 
  value, 
  onChange, 
  onSelect, 
  onMouseUp, 
  onKeyUp, 
  onFocus, 
  placeholder, 
  className, 
  readOnly = false,
  minRows = 3 
}) => {
  const textareaRef = useRef(null);

  // Auto-resize on content change
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight to fit all content
      const minHeight = minRows * 24; // Approximate line height
      textarea.style.height = `${Math.max(minHeight, textarea.scrollHeight)}px`;
    }
  }, [value, minRows]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={onChange}
      onSelect={onSelect}
      onMouseUp={onMouseUp}
      onKeyUp={onKeyUp}
      onFocus={onFocus}
      placeholder={placeholder}
      className={className}
      readOnly={readOnly}
      style={{ overflow: 'hidden' }}
    />
  );
};

// ============ HTML CLEANUP UTILITY ============
const cleanHtmlArtifacts = (text) => {
  if (!text) return '';
  // Remove common HTML artifacts
  return text
    .replace(/&nbsp;/g, ' ')           // Replace &nbsp; with space
    .replace(/<br\s*\/?>/gi, '\n')     // Replace <br> with newline
    .replace(/<div>/gi, '\n')          // Replace <div> with newline
    .replace(/<\/div>/gi, '')          // Remove closing div
    .replace(/<[^>]*>/g, '')           // Remove any remaining HTML tags
    .replace(/&lt;/g, '<')             // Decode HTML entities
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/\n{3,}/g, '\n\n')        // Collapse multiple newlines
    .trim();
};

// ============ MODE INFERENCE ============
const inferMode = (content, sections = [], isTranscript = false) => {
  const allContent = [content, ...sections.map(s => s.content)].join(' ');
  if (!allContent || allContent.trim().length === 0) return "kickstart";
  
  const wordCount = allContent.trim().split(/\s+/).filter(Boolean).length;
  
  if (isTranscript || wordCount > 500) return "mining";
  if (wordCount < 200) return "seed";
  return "seed";
};

const MODE_LABELS = {
  kickstart: { label: "Kickstart", color: "bg-purple-500/20 text-purple-400", description: "Nearly empty - let's get started" },
  seed: { label: "Seed", color: "bg-green-500/20 text-green-400", description: "Building the core idea" },
  mining: { label: "Mining", color: "bg-blue-500/20 text-blue-400", description: "Extracting gold from bulk material" }
};

// ============ CREATIVE LENSES (Static, exactly 8) ============
const CREATIVE_LENSES = [
  {
    id: "shift_expectation",
    title: "Shift Expectation",
    questions: [
      "What does the audience expect to happen next?",
      "How can you deliver something completely different?",
      "Where is the assumption you can subvert?"
    ]
  },
  {
    id: "escalate_absurdity",
    title: "Escalate Absurdity",
    questions: [
      "What if this went 10x further?",
      "Where does reality stop and your version begin?",
      "What's the most extreme logical conclusion?"
    ]
  },
  {
    id: "flip_status",
    title: "Flip Status",
    questions: [
      "Who has power here? What if they didn't?",
      "What if the expert was clueless?",
      "What if the small thing was treated as massive?"
    ]
  },
  {
    id: "make_personal",
    title: "Make It Personal",
    questions: [
      "Where's your real experience in this?",
      "What specific detail only you would know?",
      "Why does this actually bother/delight you?"
    ]
  },
  {
    id: "make_universal",
    title: "Make It Universal",
    questions: [
      "What shared experience does this tap into?",
      "Who else has felt exactly this way?",
      "What's the 'we all do this' underneath?"
    ]
  },
  {
    id: "compress",
    title: "Compress It",
    questions: [
      "What words aren't earning their place?",
      "Can the setup be one sentence shorter?",
      "What if you started at the punch?"
    ]
  },
  {
    id: "stretch",
    title: "Stretch It",
    questions: [
      "What detail could you paint more vividly?",
      "Where could an act-out live?",
      "What's missing between setup and punch?"
    ]
  },
  {
    id: "wrong_comparison",
    title: "Wrong Comparison",
    questions: [
      "What two things shouldn't be compared but are similar?",
      "What's this like that it definitely isn't?",
      "What category does this not belong in?"
    ]
  }
];

// ============ MODE HELPERS (Contextual) ============
const MODE_HELPERS = {
  kickstart: [
    { id: "bad_versions", title: "Write Bad Versions", prompt: "Write 3 intentionally bad versions. Get the obvious out of your system." },
    { id: "change_pov", title: "Change POV", prompt: "Write this from someone else's perspective entirely." },
    { id: "constraint", title: "Constraint Prompt", prompt: "Write it in exactly 10 words. Or as a question. Or without the word 'I'." }
  ],
  seed: [
    { id: "explore_angles", title: "Explore Angles", prompt: "List 5 different directions this idea could go." },
    { id: "opposite_stance", title: "Opposite Stance", prompt: "Argue the complete opposite of your current take." },
    { id: "list_assumptions", title: "List Assumptions", prompt: "What does the audience have to believe for this to work?" }
  ],
  mining: [
    { id: "emotional_spikes", title: "Highlight Emotional Spikes", prompt: "Mark the moments where energy peaks or drops." },
    { id: "extract_premises", title: "Extract Premises", prompt: "Pull out each distinct premise or idea into its own line." },
    { id: "repeated_ideas", title: "Find Repeated Ideas", prompt: "What themes or phrases appear more than once?" }
  ]
};

// ============ AI REFLECTION QUESTIONS ============
const AI_REFLECTION_QUESTIONS = [
  { id: "what_about", label: "What is this about?", description: "Uncover the core theme" },
  { id: "where_turn", label: "Where might the turn be?", description: "Find the twist or shift" },
  { id: "where_slow", label: "Where does this slow down?", description: "Identify drag points" }
];

// ============ SORTABLE SECTION CARD (for dnd-kit) ============
const SortableSectionCard = ({ 
  section, 
  idx, 
  isViewingOldVersion,
  highlightedSectionId,
  collapsedSections,
  editingSectionId,
  setEditingSectionId,
  handleToggleSectionIncluded,
  handleToggleSectionCollapse,
  handleUpdateSection,
  handleOpenSectionRemix,
  handleDuplicateSection,
  handlePromoteSectionToBit,
  handleDeleteSection,
  handleTextareaSelect,
  handleSectionFocus
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.section_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`border rounded-sm transition-all ${
        highlightedSectionId === section.section_id
          ? 'border-primary border-2'
          : section.included === false
          ? 'border-border border-dashed bg-muted/20'
          : 'border-border bg-card'
      }`}
    >
      {/* Section header row */}
      <div className={`flex items-center gap-2 p-2 hover:bg-muted/30 transition-colors ${section.included === false ? 'opacity-60' : ''}`}>
        <button 
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded-sm text-muted-foreground hover:text-foreground"
          disabled={isViewingOldVersion}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        
        {/* Include/Exclude toggle */}
        {!isViewingOldVersion && (
          <button
            onClick={() => handleToggleSectionIncluded(section.section_id)}
            className={`p-1 rounded-sm transition-colors ${
              section.included === false 
                ? 'text-muted-foreground hover:text-foreground hover:bg-muted' 
                : 'text-primary hover:bg-primary/10'
            }`}
            title={section.included === false ? "Include in script" : "Exclude from script"}
          >
            {section.included === false ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>
        )}
        
        <button
          onClick={() => handleToggleSectionCollapse(section.section_id)}
          className="p-1 hover:bg-muted rounded-sm"
        >
          {collapsedSections.has(section.section_id) ? (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
        
        {editingSectionId === section.section_id ? (
          <Input
            value={section.title}
            onChange={(e) => handleUpdateSection(section.section_id, { title: e.target.value })}
            onBlur={() => setEditingSectionId(null)}
            onKeyDown={(e) => e.key === 'Enter' && setEditingSectionId(null)}
            className="h-7 text-sm flex-1"
            autoFocus
          />
        ) : (
          <span 
            className={`text-sm font-medium flex-1 cursor-pointer hover:text-primary truncate ${
              section.included === false ? 'line-through text-muted-foreground' : ''
            }`}
            onClick={() => !isViewingOldVersion && setEditingSectionId(section.section_id)}
          >
            {section.title || `Section ${idx + 1}`}
            {section.included === false && (
              <span className="ml-2 text-xs font-normal no-underline">(excluded)</span>
            )}
          </span>
        )}
        
        {!isViewingOldVersion && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleToggleSectionIncluded(section.section_id)}>
                {section.included === false ? (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    Include in Script
                  </>
                ) : (
                  <>
                    <EyeOff className="w-4 h-4 mr-2" />
                    Exclude from Script
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleOpenSectionRemix(section)}>
                <Sparkles className="w-4 h-4 mr-2" />
                Remix Section
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setEditingSectionId(section.section_id)}>
                <Pencil className="w-4 h-4 mr-2" />
                Rename Section
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDuplicateSection(section)}>
                <Copy className="w-4 h-4 mr-2" />
                Duplicate Section
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handlePromoteSectionToBit(section)}>
                <FileUp className="w-4 h-4 mr-2" />
                Promote to Bit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => handleDeleteSection(section.section_id)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Section
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      
      {/* Section content */}
      {!collapsedSections.has(section.section_id) && (
        <div className="p-3 border-t border-border">
          <AutoResizeTextarea
            value={section.content}
            onChange={(e) => !isViewingOldVersion && handleUpdateSection(section.section_id, { content: e.target.value })}
            onSelect={(e) => !isViewingOldVersion && handleTextareaSelect(e, section.section_id)}
            onMouseUp={(e) => !isViewingOldVersion && handleTextareaSelect(e, section.section_id)}
            onKeyUp={(e) => !isViewingOldVersion && handleTextareaSelect(e, section.section_id)}
            onFocus={() => handleSectionFocus(section.section_id)}
            placeholder="Section content..."
            className="w-full font-mono text-sm resize-none border-0 focus-visible:ring-0 bg-transparent p-0 focus:outline-none"
            readOnly={isViewingOldVersion}
            minRows={3}
          />
        </div>
      )}
    </div>
  );
};

// ============ SIMPLE RICH TEXT EDITOR ============
const SimpleEditor = ({ 
  value, 
  onChange, 
  onSelectionChange, 
  placeholder = "Start writing...",
  readOnly = false,
  className = ""
}) => {
  const editorRef = useRef(null);

  const handleSelectionChange = useCallback(() => {
    if (!editorRef.current) return;
    
    const sel = window.getSelection();
    if (sel.rangeCount > 0 && sel.toString().trim()) {
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const editorRect = editorRef.current.getBoundingClientRect();
      
      // Get the selected text
      const selectedText = sel.toString();
      
      // Get the full text content for position calculation
      const fullText = editorRef.current.innerText || editorRef.current.textContent || '';
      
      // Find the selection position in the full text
      // Use a more robust approach - get text before the selection
      const preSelectionRange = range.cloneRange();
      preSelectionRange.selectNodeContents(editorRef.current);
      preSelectionRange.setEnd(range.startContainer, range.startOffset);
      const startOffset = preSelectionRange.toString().length;
      
      onSelectionChange?.({
        text: selectedText,
        fullText: fullText,
        start: startOffset,
        end: startOffset + selectedText.length,
        position: {
          top: rect.top - editorRect.top,
          left: rect.left - editorRect.left + rect.width / 2
        }
      });
    } else {
      onSelectionChange?.(null);
    }
  }, [onSelectionChange]);

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [handleSelectionChange]);

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      // Use innerText to get plain text, avoiding HTML artifacts
      // Convert <br> and block elements to newlines
      const text = editorRef.current.innerText || '';
      onChange(text);
    }
  }, [onChange]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  useEffect(() => {
    if (editorRef.current) {
      // Only update if the text content is actually different
      // This preserves cursor position when possible
      const currentText = editorRef.current.innerText || '';
      if (currentText !== value) {
        editorRef.current.innerText = value || '';
      }
    }
  }, [value]);

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {!readOnly && (
        <div className="flex items-center gap-1 p-2 border-b border-border bg-muted/30">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 font-bold"
            onClick={() => execCommand('bold')}
            title="Bold"
          >
            B
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 italic"
            onClick={() => execCommand('italic')}
            title="Italic"
          >
            I
          </Button>
          <div className="w-px h-4 bg-border mx-1" />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => execCommand('insertUnorderedList')}
            title="Bullet List"
          >
            •
          </Button>
        </div>
      )}
      
      <div className="flex-1 relative overflow-hidden">
        <div
          ref={editorRef}
          contentEditable={!readOnly}
          onInput={handleInput}
          onPaste={handlePaste}
          className={`
            h-full w-full p-4 font-mono text-base leading-relaxed overflow-auto
            focus:outline-none
            ${readOnly ? 'bg-muted/20' : 'bg-background/50'}
            [&>ul]:list-disc [&>ul]:ml-6 [&>ol]:list-decimal [&>ol]:ml-6
          `}
          data-placeholder={placeholder}
          style={{ minHeight: '300px' }}
          suppressContentEditableWarning
        />
        
        {(!value || value === '<br>') && (
          <div className="absolute top-4 left-4 text-muted-foreground pointer-events-none font-mono">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
};

// ============ HELPER: Strip HTML to plain text ============
const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
};

// ============ HELPER: Generate section title from content ============
const generateSectionTitle = (content) => {
  const plain = stripHtml(content);
  const firstLine = plain.split('\n')[0].trim();
  return firstLine.length > 50 ? firstLine.substring(0, 47) + '...' : firstLine || 'Untitled Section';
};

// ============ MAIN WORKSHOP COMPONENT ============
export const BitWorkshop = ({
  bit,
  categories,
  onSave,
  onAutoSave,
  onClose,
  onExtractSelection,
  onPromoteToBit,
  onDuplicate,
  onCreateNewBit,  // New prop for creating new bits
  initialContent = "",
  initialIdea = ""
}) => {
  // ============ STATE ============
  const [title, setTitle] = useState("");
  const [content, setContent] = useState(""); // Pre-section body text
  const [sections, setSections] = useState([]); // Post-section: canonical draft
  const [originalSnapshot, setOriginalSnapshot] = useState(null); // Read-only original
  const [status, setStatus] = useState("working");
  const [category, setCategory] = useState("");
  const [notes, setNotes] = useState("");
  const [inlineNotes, setInlineNotes] = useState([]);
  const [originalIdeas, setOriginalIdeas] = useState([]);
  const [currentBitId, setCurrentBitId] = useState(null); // Track current bit ID for new bits
  
  // UI State
  const [isCoachShelfOpen, setIsCoachShelfOpen] = useState(false);
  const [viewMode, setViewMode] = useState("outline"); // "outline" | "script"
  const [draftMode, setDraftMode] = useState("draft"); // "draft" | "original" toggle
  const [selectionInfo, setSelectionInfo] = useState(null); // {text, start, end, sectionId?}
  const [showSelectionActions, setShowSelectionActions] = useState(false);
  const [isRemixOpen, setIsRemixOpen] = useState(false);
  const [remixTargetSection, setRemixTargetSection] = useState(null);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteSelection, setNoteSelection] = useState("");
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [highlightedSectionId, setHighlightedSectionId] = useState(null); // For highlighting new sections
  
  // Coach Shelf State
  const [activeLens, setActiveLens] = useState(null);
  const [aiReflectionEnabled, setAiReflectionEnabled] = useState(false);
  const [isReflecting, setIsReflecting] = useState(false);
  const [currentReflection, setCurrentReflection] = useState(null);
  const [activeHelper, setActiveHelper] = useState(null);
  
  // Version History State
  const [versions, setVersions] = useState([]);
  const [viewingVersion, setViewingVersion] = useState(null);
  const [currentDataBackup, setCurrentDataBackup] = useState(null);
  
  // Auto-save state
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const lastSavedDataRef = useRef(null);

  // DnD sensors for section reordering
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ============ COMPUTED VALUES ============
  
  // Determine if we're in post-section state (sections own all text)
  const isPostSectionState = sections.length > 0;
  
  // Word count from either content or sections
  const wordCount = useMemo(() => {
    if (isPostSectionState) {
      const sectionText = sections.map(s => stripHtml(s.content)).join(' ');
      return sectionText ? sectionText.split(/\s+/).filter(Boolean).length : 0;
    } else {
      const mainText = stripHtml(content);
      return mainText ? mainText.split(/\s+/).filter(Boolean).length : 0;
    }
  }, [content, sections, isPostSectionState]);

  const mode = useMemo(() => inferMode(content, sections), [content, sections]);
  const modeInfo = MODE_LABELS[mode];
  const modeHelpers = MODE_HELPERS[mode] || [];
  const isViewingOldVersion = !!viewingVersion;
  const hasOriginalSnapshot = !!originalSnapshot;
  
  // Content for Remix
  const remixContent = useMemo(() => {
    if (remixTargetSection) {
      return remixTargetSection.content || '';
    }
    if (isPostSectionState) {
      return sections.map(s => s.content).join('\n\n');
    }
    return content;
  }, [remixTargetSection, content, sections, isPostSectionState]);

  // ============ INITIALIZATION ============
  useEffect(() => {
    if (bit) {
      setTitle(bit.title || "");
      // Auto-convert content to a single section if bit has content but no sections
      const existingSections = bit.sections || [];
      const bitContent = cleanHtmlArtifacts(bit.content || "");
      
      if (existingSections.length === 0 && bitContent.trim()) {
        // Convert body content to a single section
        const autoSection = {
          section_id: `sec_${Date.now()}_main`,
          title: bit.title || generateSectionTitle(bitContent),
          content: bitContent,
          order: 0,
          included: true
        };
        setSections([autoSection]);
        setContent(""); // Clear content since sections now own it
        setOriginalSnapshot(bitContent); // Preserve original as snapshot
      } else {
        setContent(bitContent);
        // Ensure all sections have 'included' field and clean HTML artifacts
        setSections(existingSections.map(s => ({ 
          ...s, 
          content: cleanHtmlArtifacts(s.content || ''),
          included: s.included !== false 
        })));
        setOriginalSnapshot(bit.original_snapshot || null);
      }
      
      setStatus(bit.status || "working");
      setCategory(bit.category || "");
      setNotes(bit.notes || "");
      setInlineNotes(bit.inline_notes || []);
      setOriginalIdeas(bit.original_ideas || []);
      lastSavedDataRef.current = JSON.stringify({
        content: bit.content,
        sections: bit.sections || [],
        original_snapshot: bit.original_snapshot,
        title: bit.title,
        status: bit.status,
        notes: bit.notes,
        inlineNotes: bit.inline_notes
      });
      setViewingVersion(null);
      setCurrentDataBackup(null);
      setCollapsedSections(new Set());
      setDraftMode("draft");
      
      fetch(`${API}/bits/${bit.bit_id}/versions`, { credentials: 'include' })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data?.versions) {
            setVersions(data.versions.reverse());
          }
        })
        .catch(() => {});
    } else {
      const newContent = cleanHtmlArtifacts(initialContent || "");
      
      // If we have initial content, auto-create a section and generate title from first line
      if (newContent.trim()) {
        const autoTitle = generateSectionTitle(newContent);
        setTitle(autoTitle);
        
        const autoSection = {
          section_id: `sec_${Date.now()}_main`,
          title: autoTitle,
          content: newContent,
          order: 0,
          included: true
        };
        setSections([autoSection]);
        setContent("");
        setOriginalSnapshot(newContent);
      } else {
        setTitle("");
        setContent("");
        setSections([]);
        setOriginalSnapshot(null);
      }
      
      setStatus("working");
      setCategory("");
      setNotes("");
      setInlineNotes([]);
      setOriginalIdeas(initialIdea ? [initialIdea] : []);
      lastSavedDataRef.current = null;
      setVersions([]);
      setViewingVersion(null);
      setCurrentDataBackup(null);
      setCollapsedSections(new Set());
      setDraftMode("draft");
      setCurrentBitId(null); // Reset for new bits
    }
  }, [bit, initialContent, initialIdea]);

  // Clear highlight after 2 seconds
  useEffect(() => {
    if (highlightedSectionId) {
      const timer = setTimeout(() => setHighlightedSectionId(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [highlightedSectionId]);

  // ============ VERSION HISTORY HANDLERS ============
  const handleViewVersion = (version, index) => {
    setCurrentDataBackup({
      title,
      content,
      sections,
      originalSnapshot,
      notes,
      status
    });
    
    setTitle(version.title || "");
    setContent(version.content || "");
    setNotes(version.notes || "");
    setStatus(version.status || "working");
    setSections(version.sections || []);
    setViewingVersion({ ...version, index: versions.length - index });
  };

  const handleBackToCurrentVersion = () => {
    if (currentDataBackup) {
      setTitle(currentDataBackup.title);
      setContent(currentDataBackup.content);
      setSections(currentDataBackup.sections || []);
      setOriginalSnapshot(currentDataBackup.originalSnapshot);
      setNotes(currentDataBackup.notes);
      setStatus(currentDataBackup.status);
    }
    setViewingVersion(null);
    setCurrentDataBackup(null);
  };

  const handleRestoreVersion = () => {
    setViewingVersion(null);
    setCurrentDataBackup(null);
    toast.success("Version restored - save to keep changes");
  };

  // ============ SECTION EXTRACTION HANDLERS ============
  
  /**
   * EXTRACT AS SECTION - Split-based conversion
   * 
   * Pre-section state (content exists, no sections):
   *   - Save original_snapshot
   *   - Split content into [before, selected, after]
   *   - Convert to sections at those positions
   *   - Clear content
   * 
   * Post-section state (sections exist):
   *   - Split the active section into [before, selected, after]
   *   - Replace original section with before
   *   - Insert new section with selected immediately after
   *   - Keep after in the original section OR create third section
   */
  const handleExtractAsSection = () => {
    if (!selectionInfo?.text) return;
    
    const selectedText = selectionInfo.text;
    const selectedTitle = generateSectionTitle(selectedText);
    
    if (!isPostSectionState) {
      // ========== PRE-SECTION STATE: First extraction ==========
      // Save original snapshot before any conversion
      if (!originalSnapshot) {
        setOriginalSnapshot(content);
      }
      
      // Use the full text from selection info (which comes from the editor's innerText)
      const fullText = selectionInfo.fullText || stripHtml(content);
      const selStart = selectionInfo.start;
      const selEnd = selectionInfo.end;
      
      // Validate we have valid positions
      if (selStart === undefined || selStart < 0 || selEnd === undefined) {
        toast.error("Could not determine selection position");
        return;
      }
      
      // Split into before, selected, after using character positions
      const beforeText = fullText.substring(0, selStart).trim();
      const afterText = fullText.substring(selEnd).trim();
      
      // Build sections array
      const newSections = [];
      let order = 0;
      
      if (beforeText) {
        newSections.push({
          section_id: `sec_${Date.now()}_before`,
          title: generateSectionTitle(beforeText),
          content: beforeText,
          order: order++,
          included: true
        });
      }
      
      // The extracted section
      const extractedSection = {
        section_id: `sec_${Date.now()}_extracted`,
        title: selectedTitle,
        content: selectedText,
        order: order++,
        included: true
      };
      newSections.push(extractedSection);
      
      if (afterText) {
        newSections.push({
          section_id: `sec_${Date.now()}_after`,
          title: generateSectionTitle(afterText),
          content: afterText,
          order: order++,
          included: true
        });
      }
      
      // Switch to post-section state
      setSections(newSections);
      setContent(""); // Clear content - sections now own all text
      setHighlightedSectionId(extractedSection.section_id);
      
      toast.success("Draft converted to sections. Original preserved.");
      
    } else {
      // ========== POST-SECTION STATE: Extraction within a section ==========
      const sectionId = selectionInfo.sectionId || activeSectionId;
      
      if (!sectionId) {
        toast.error("Please select text within a section");
        return;
      }
      
      const sectionIndex = sections.findIndex(s => s.section_id === sectionId);
      if (sectionIndex === -1) {
        toast.error("Could not find the section");
        return;
      }
      
      const section = sections[sectionIndex];
      const sectionContent = section.content || '';
      
      // For textarea selections, we need to find the position differently
      // Use the selection start/end if available, otherwise try to find the text
      let selStart = selectionInfo.start;
      let selEnd = selectionInfo.end;
      
      // If positions aren't available (textarea selection), find the text
      if (selStart === undefined || selEnd === undefined) {
        selStart = sectionContent.indexOf(selectedText);
        if (selStart === -1) {
          // Try trimmed version
          const trimmedSelection = selectedText.trim();
          selStart = sectionContent.indexOf(trimmedSelection);
          if (selStart === -1) {
            toast.error("Could not find selection in section");
            return;
          }
          selEnd = selStart + trimmedSelection.length;
        } else {
          selEnd = selStart + selectedText.length;
        }
      }
      
      // Split the section content using positions
      const beforeText = sectionContent.substring(0, selStart).trim();
      const afterText = sectionContent.substring(selEnd).trim();
      
      // Build replacement sections
      const newSections = [...sections];
      const insertIndex = sectionIndex;
      
      // Remove the original section
      newSections.splice(insertIndex, 1);
      
      let insertAt = insertIndex;
      
      // Insert "before" section (update original if content exists)
      if (beforeText) {
        newSections.splice(insertAt++, 0, {
          section_id: section.section_id, // Keep original ID for "before"
          title: section.title,
          content: beforeText,
          order: 0, // Will be recalculated
          included: section.included !== false
        });
      }
      
      // Insert extracted section
      const extractedSection = {
        section_id: `sec_${Date.now()}_extracted`,
        title: selectedTitle,
        content: selectedText,
        order: 0,
        included: true
      };
      newSections.splice(insertAt++, 0, extractedSection);
      
      // Insert "after" section if content exists
      if (afterText) {
        newSections.splice(insertAt++, 0, {
          section_id: `sec_${Date.now()}_after`,
          title: generateSectionTitle(afterText),
          content: afterText,
          order: 0,
          included: true
        });
      }
      
      // Recalculate order
      const reorderedSections = newSections.map((s, i) => ({ ...s, order: i }));
      
      setSections(reorderedSections);
      setHighlightedSectionId(extractedSection.section_id);
      
      toast.success("Text extracted as new section");
    }
    
    // Clear selection state (stay in current view)
    setShowSelectionActions(false);
    setSelectionInfo(null);
  };

  const handlePromoteTobit = () => {
    if (!selectionInfo?.text || !onPromoteToBit) return;
    onPromoteToBit(selectionInfo.text);
    setShowSelectionActions(false);
    setSelectionInfo(null);
  };

  const handleAddSection = () => {
    const newSection = {
      section_id: `sec_${Date.now()}`,
      title: `Section ${sections.length + 1}`,
      content: "",
      order: sections.length,
      included: true
    };
    setSections([...sections, newSection]);
    setActiveSectionId(newSection.section_id);
    setHighlightedSectionId(newSection.section_id);
  };

  const handleUpdateSection = (sectionId, updates) => {
    setSections(sections.map(s => 
      s.section_id === sectionId ? { ...s, ...updates } : s
    ));
  };

  const handleToggleSectionIncluded = (sectionId) => {
    setSections(sections.map(s => 
      s.section_id === sectionId ? { ...s, included: !s.included } : s
    ));
  };

  const handleDeleteSection = (sectionId) => {
    const newSections = sections.filter(s => s.section_id !== sectionId).map((s, i) => ({ ...s, order: i }));
    setSections(newSections);
    
    // If all sections deleted, user can add content again (but original_snapshot is preserved)
  };

  const handleDuplicateSection = (section) => {
    const newSection = {
      section_id: `sec_${Date.now()}`,
      title: `${section.title || 'Section'} (copy)`,
      content: section.content,
      order: section.order + 1,
      included: section.included !== false
    };
    
    const newSections = [...sections];
    const idx = newSections.findIndex(s => s.section_id === section.section_id);
    newSections.splice(idx + 1, 0, newSection);
    setSections(newSections.map((s, i) => ({ ...s, order: i })));
    toast.success("Section duplicated");
  };

  const handleToggleSectionCollapse = (sectionId) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  // ============ DRAG AND DROP HANDLERS (using @dnd-kit) ============
  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const oldIndex = sections.findIndex(s => s.section_id === active.id);
    const newIndex = sections.findIndex(s => s.section_id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    const newSections = arrayMove(sections, oldIndex, newIndex).map((s, i) => ({
      ...s,
      order: i
    }));
    
    setSections(newSections);
  };

  // ============ REMIX HANDLERS ============
  const handleOpenSectionRemix = (section) => {
    setRemixTargetSection(section);
    setIsRemixOpen(true);
  };

  const handleSectionFocus = (sectionId) => {
    setActiveSectionId(sectionId);
  };

  // ============ PRINT FUNCTION ============
  const handlePrint = (mode) => {
    const includedSections = sections
      .filter(s => s.included !== false)
      .sort((a, b) => a.order - b.order);
    
    let printContent = '';
    
    if (mode === "script") {
      // Script: Just the full text, no sections
      printContent = `
        <html>
        <head>
          <title>${title || 'Untitled Bit'}</title>
          <style>
            body { 
              font-family: 'Courier New', monospace; 
              max-width: 700px; 
              margin: 40px auto; 
              padding: 20px;
              line-height: 1.6;
              font-size: 14px;
            }
            h1 { 
              font-size: 24px; 
              margin-bottom: 30px; 
              text-align: center;
              font-family: Georgia, serif;
            }
            .content { white-space: pre-wrap; }
          </style>
        </head>
        <body>
          <h1>${title || 'Untitled Bit'}</h1>
          <div class="content">${includedSections.map(s => s.content).join('\n\n')}</div>
        </body>
        </html>
      `;
    } else {
      // Outline: Show sections with titles
      printContent = `
        <html>
        <head>
          <title>${title || 'Untitled Bit'} - Outline</title>
          <style>
            body { 
              font-family: Georgia, serif; 
              max-width: 700px; 
              margin: 40px auto; 
              padding: 20px;
              line-height: 1.6;
            }
            h1 { 
              font-size: 24px; 
              margin-bottom: 30px; 
              text-align: center;
            }
            .section { 
              margin-bottom: 30px; 
              padding-bottom: 20px;
              border-bottom: 1px solid #eee;
            }
            .section:last-child { border-bottom: none; }
            .section-title { 
              font-size: 14px; 
              font-weight: bold; 
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #666;
              margin-bottom: 10px;
            }
            .section-content { 
              font-family: 'Courier New', monospace; 
              white-space: pre-wrap;
              font-size: 13px;
            }
          </style>
        </head>
        <body>
          <h1>${title || 'Untitled Bit'}</h1>
          ${includedSections.map((s, i) => `
            <div class="section">
              <div class="section-title">${s.title || `Section ${i + 1}`}</div>
              <div class="section-content">${s.content}</div>
            </div>
          `).join('')}
        </body>
        </html>
      `;
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handlePromoteSectionToBit = (section) => {
    if (!onPromoteToBit) return;
    onPromoteToBit(section.content, section.title);
    handleDeleteSection(section.section_id);
    toast.success("Section promoted to new Bit");
  };

  // ============ SELECTION HANDLING ============
  const handleSelectionChange = useCallback((info, sectionId = null) => {
    if (info && info.text && info.text.trim()) {
      setSelectionInfo({ ...info, sectionId });
      setShowSelectionActions(true);
    } else {
      setShowSelectionActions(false);
      setSelectionInfo(null);
    }
  }, []);

  // Handle selection in textarea (for sections in post-section state)
  const handleTextareaSelect = useCallback((e, sectionId) => {
    const textarea = e.target;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    
    if (selectedText && selectedText.trim()) {
      setSelectionInfo({
        text: selectedText,
        fullText: textarea.value,
        start: start,
        end: end,
        sectionId: sectionId
      });
      setShowSelectionActions(true);
      setActiveSectionId(sectionId);
    } else {
      setShowSelectionActions(false);
      setSelectionInfo(null);
    }
  }, []);

  const handleAddNote = () => {
    if (!selectionInfo?.text) return;
    setNoteSelection(selectionInfo.text);
    setNoteText("");
    setIsNoteDialogOpen(true);
    setShowSelectionActions(false);
  };

  const handleSaveNote = () => {
    if (!noteText.trim() || !noteSelection) return;
    
    const newNote = {
      note_id: `note_${Date.now()}`,
      selected_text: noteSelection,
      text: noteText.trim(),
      created_at: new Date().toISOString()
    };
    
    setInlineNotes([...inlineNotes, newNote]);
    setIsNoteDialogOpen(false);
    setNoteText("");
    setNoteSelection("");
    toast.success("Note added");
  };

  // ============ AI REFLECTION ============
  const handleAIReflection = async (questionType) => {
    if (!aiReflectionEnabled) return;
    
    const textToAnalyze = isPostSectionState 
      ? sections.map(s => stripHtml(s.content)).join('\n\n')
      : stripHtml(content);
    
    if (!textToAnalyze.trim()) return;
    
    setIsReflecting(true);
    setCurrentReflection(null);
    
    try {
      const response = await fetch(`${API}/bits/reflect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          bit_id: bit?.bit_id || 'new',
          content: textToAnalyze,
          question_type: questionType
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentReflection(data);
      } else {
        toast.error("Failed to get reflection");
      }
    } catch (error) {
      toast.error("Failed to get reflection");
    } finally {
      setIsReflecting(false);
    }
  };

  // ============ AUTO-SAVE ============
  const currentDataString = useMemo(() => {
    return JSON.stringify({ content, sections, originalSnapshot, title, status, notes, inlineNotes });
  }, [content, sections, originalSnapshot, title, status, notes, inlineNotes]);

  // Get the effective bit ID (from prop or from newly created bit)
  const effectiveBitId = bit?.bit_id || currentBitId;

  // Create new bit when content is first added (for new bits only)
  const createNewBitIfNeeded = useCallback(async () => {
    // Only create if: no existing bit, no currentBitId, and has meaningful content
    if (bit?.bit_id || currentBitId) return null;
    
    const hasContent = (content && stripHtml(content).trim().length > 0) || 
                       (sections.length > 0 && sections.some(s => stripHtml(s.content).trim().length > 0));
    
    if (!hasContent || !onCreateNewBit) return null;
    
    try {
      const newBit = await onCreateNewBit({
        title: title || "Untitled",
        content,
        sections,
        original_snapshot: originalSnapshot,
        status,
        category: category || null,
        notes,
        inline_notes: inlineNotes,
        original_ideas: originalIdeas
      });
      
      if (newBit?.bit_id) {
        setCurrentBitId(newBit.bit_id);
        lastSavedDataRef.current = currentDataString;
        setLastSaved(new Date());
        return newBit.bit_id;
      }
    } catch (error) {
      console.error("Failed to create new bit:", error);
    }
    return null;
  }, [bit?.bit_id, currentBitId, content, sections, title, originalSnapshot, status, category, notes, inlineNotes, originalIdeas, onCreateNewBit, currentDataString]);

  const performAutoSave = useCallback(async () => {
    if (isViewingOldVersion) return;
    
    // If no bit exists yet, try to create one
    if (!effectiveBitId) {
      await createNewBitIfNeeded();
      return;
    }
    
    if (!onAutoSave) return;
    if (currentDataString === lastSavedDataRef.current) return;
    
    setIsSaving(true);
    try {
      await onAutoSave(effectiveBitId, {
        title,
        content,
        sections,
        original_snapshot: originalSnapshot,
        status,
        category: category || null,
        notes,
        inline_notes: inlineNotes,
        original_ideas: originalIdeas
      });
      lastSavedDataRef.current = currentDataString;
      setLastSaved(new Date());
    } catch (error) {
      console.error("Auto-save failed:", error);
    } finally {
      setIsSaving(false);
    }
  }, [effectiveBitId, title, content, sections, originalSnapshot, status, category, notes, inlineNotes, originalIdeas, onAutoSave, isViewingOldVersion, currentDataString, createNewBitIfNeeded]);

  useEffect(() => {
    // Check if there's content to save
    const hasContent = (content && stripHtml(content).trim().length > 0) || 
                       (sections.length > 0 && sections.some(s => s.content && stripHtml(s.content).trim().length > 0));
    
    if (!hasContent) return;
    
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Shorter delay for new bits to ensure quick save
    const delay = effectiveBitId ? 2000 : 1000;
    autoSaveTimerRef.current = setTimeout(performAutoSave, delay);
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [currentDataString, performAutoSave, effectiveBitId, content, sections]);

  // ============ SAVE HANDLER ============
  const handleSave = () => {
    onSave({
      title,
      content,
      sections,
      original_snapshot: originalSnapshot,
      status,
      category: category || null,
      notes,
      inline_notes: inlineNotes,
      original_ideas: originalIdeas,
      create_version: !!bit
    });
  };

  // ============ DUPLICATE HANDLER ============
  const handleDuplicate = () => {
    if (!onDuplicate) return;
    
    const duplicateData = {
      title: `${title || 'Untitled'} (Copy)`,
      content,
      sections: sections.map(s => ({
        ...s,
        section_id: `sec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      })),
      original_snapshot: originalSnapshot,
      status: "working", // Reset to working status
      category: category || null,
      notes,
      inline_notes: [...inlineNotes],
      original_ideas: [...originalIdeas]
    };
    
    onDuplicate(duplicateData);
  };

  // ============ PRINT HANDLER ============
  // ============ RENDER ============
  return (
    <div className="fixed inset-0 bg-background z-50 flex flex-col">
      {/* Top Bar */}
      <header className="flex items-center justify-between p-3 border-b border-border bg-card">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          
          <div className="h-4 w-px bg-border" />
          
          <Badge className={`${modeInfo.color} text-xs`}>
            {modeInfo.label}
          </Badge>
          
          {isPostSectionState && (
            <Badge variant="outline" className="text-xs gap-1">
              <Layers className="w-3 h-3" />
              {sections.length} section{sections.length !== 1 ? 's' : ''}
            </Badge>
          )}
          
          <span className="text-xs text-muted-foreground">
            {wordCount} words
          </span>
          
          <div className="h-4 w-px bg-border" />
          
          {/* Status Selector */}
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-7 w-[120px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="working">Working</SelectItem>
              <SelectItem value="stage-ready">Stage Ready</SelectItem>
              <SelectItem value="retired">Retired</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Category Selector */}
          {categories && categories.length > 0 && (
            <Select value={category || "none"} onValueChange={(val) => setCategory(val === "none" ? "" : val)}>
              <SelectTrigger className="h-7 w-[130px] text-xs">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Category</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.category_id} value={cat.name}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {isSaving ? (
              <span className="flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving...
              </span>
            ) : lastSaved ? (
              <span>Saved</span>
            ) : null}
          </span>
          
          {/* More options dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Print for Rehearsal
              </DropdownMenuItem>
              {bit && onDuplicate && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleDuplicate}>
                    <Copy className="w-4 h-4 mr-2" />
                    Duplicate Bit
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button onClick={handleSave} className="bg-primary text-primary-foreground gap-2">
            <Save className="w-4 h-4" />
            {bit ? "Save Version" : "Save Bit"}
          </Button>
        </div>
      </header>

      {/* Main Content - Two Panel Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Coach Shelf (Left Panel) */}
        <aside className={`
          border-r border-border bg-card transition-all duration-300 flex flex-col
          ${isCoachShelfOpen ? 'w-80' : 'w-12'}
        `}>
          <button
            onClick={() => setIsCoachShelfOpen(!isCoachShelfOpen)}
            className="p-3 border-b border-border hover:bg-muted/50 flex items-center justify-center"
          >
            {isCoachShelfOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          
          {isCoachShelfOpen && (
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-6">
                {/* Creative Lenses */}
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="flex items-center justify-between w-full text-left">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Creative Lenses
                    </span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3 space-y-2">
                    {CREATIVE_LENSES.map((lens) => (
                      <Collapsible key={lens.id}>
                        <CollapsibleTrigger 
                          className={`
                            w-full text-left p-2 rounded-sm text-sm transition-colors
                            ${activeLens === lens.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'}
                          `}
                          onClick={() => setActiveLens(activeLens === lens.id ? null : lens.id)}
                        >
                          {lens.title}
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-1 ml-2 space-y-1">
                          {lens.questions.map((q, i) => (
                            <p key={i} className="text-xs text-muted-foreground py-1 px-2 bg-muted/30 rounded">
                              {q}
                            </p>
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    ))}
                  </CollapsibleContent>
                </Collapsible>

                {/* Mode Helpers */}
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="flex items-center justify-between w-full text-left">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {modeInfo.label} Helpers
                    </span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3 space-y-2">
                    {modeHelpers.map((helper) => (
                      <Collapsible key={helper.id}>
                        <CollapsibleTrigger 
                          className={`
                            w-full text-left p-2 rounded-sm text-sm transition-colors
                            ${activeHelper === helper.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'}
                          `}
                          onClick={() => setActiveHelper(activeHelper === helper.id ? null : helper.id)}
                        >
                          {helper.title}
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-1 ml-2">
                          <p className="text-xs text-muted-foreground py-2 px-2 bg-muted/30 rounded">
                            {helper.prompt}
                          </p>
                        </CollapsibleContent>
                      </Collapsible>
                    ))}
                  </CollapsibleContent>
                </Collapsible>

                {/* AI Reflection */}
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full text-left">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      AI Reflection
                    </span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3 space-y-3">
                    <button
                      onClick={() => setAiReflectionEnabled(!aiReflectionEnabled)}
                      className="flex items-center gap-2 text-sm w-full p-2 rounded-sm hover:bg-muted/50"
                    >
                      {aiReflectionEnabled ? (
                        <ToggleRight className="w-5 h-5 text-primary" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-muted-foreground" />
                      )}
                      <span className={aiReflectionEnabled ? 'text-primary' : 'text-muted-foreground'}>
                        {aiReflectionEnabled ? "Enabled" : "Enable AI Reflection"}
                      </span>
                    </button>
                    
                    {aiReflectionEnabled && (
                      <>
                        <p className="text-xs text-muted-foreground italic">
                          AI helps you see your work - never writes for you.
                        </p>
                        
                        <div className="space-y-2">
                          {AI_REFLECTION_QUESTIONS.map((q) => (
                            <button
                              key={q.id}
                              onClick={() => handleAIReflection(q.id)}
                              disabled={isReflecting}
                              className="w-full text-left p-2 rounded-sm text-sm bg-muted/30 hover:bg-muted/50 disabled:opacity-50"
                            >
                              <span className="font-medium">{q.label}</span>
                              <p className="text-xs text-muted-foreground">{q.description}</p>
                            </button>
                          ))}
                        </div>
                        
                        {isReflecting && (
                          <div className="flex items-center justify-center p-4">
                            <Loader2 className="w-5 h-5 animate-spin text-primary" />
                          </div>
                        )}
                        
                        {currentReflection && (
                          <div className="p-3 bg-primary/10 rounded-sm border border-primary/30">
                            <p className="text-sm">{currentReflection.reflection}</p>
                          </div>
                        )}
                      </>
                    )}
                  </CollapsibleContent>
                </Collapsible>

                {/* Inline Notes */}
                {inlineNotes.length > 0 && (
                  <Collapsible defaultOpen>
                    <CollapsibleTrigger className="flex items-center justify-between w-full text-left">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Notes ({inlineNotes.length})
                      </span>
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 space-y-2">
                      {inlineNotes.map((note) => (
                        <div key={note.note_id} className="p-2 bg-muted/30 rounded-sm text-xs">
                          <p className="text-muted-foreground italic mb-1">&ldquo;{note.selected_text}&rdquo;</p>
                          <p>{note.text}</p>
                        </div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            </ScrollArea>
          )}
        </aside>

        {/* Writing Canvas (Right Panel) */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Canvas Header */}
          <div className="flex items-center justify-between p-3 border-b border-border bg-muted/30">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Untitled Bit"
              className="max-w-md bg-transparent border-0 text-lg font-semibold focus-visible:ring-0 px-0"
            />
            
            <div className="flex items-center gap-2">
              {/* Draft/Original Toggle (only visible when original_snapshot exists) */}
              {hasOriginalSnapshot && (
                <>
                  <Button
                    variant={draftMode === "draft" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setDraftMode("draft")}
                    className="gap-1"
                  >
                    <FileText className="w-4 h-4" />
                    Draft
                  </Button>
                  <Button
                    variant={draftMode === "original" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setDraftMode("original")}
                    className="gap-1"
                  >
                    <Archive className="w-4 h-4" />
                    Original
                  </Button>
                  <div className="w-px h-4 bg-border mx-2" />
                </>
              )}
              
              {/* View Toggle (Outline/Script) - only for Draft mode */}
              {draftMode === "draft" && (
                <>
                  <Button
                    variant={viewMode === "outline" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("outline")}
                    className="gap-1"
                  >
                    <ListOrdered className="w-4 h-4" />
                    Outline
                  </Button>
                  <Button
                    variant={viewMode === "script" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("script")}
                    className="gap-1"
                  >
                    <Eye className="w-4 h-4" />
                    Script
                  </Button>
                  <div className="w-px h-4 bg-border mx-2" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePrint(viewMode)}
                    className="gap-1"
                    title={`Print ${viewMode === "script" ? "Script" : "Outline"}`}
                  >
                    <Printer className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Original Spark */}
          {originalIdeas.length > 0 && (
            <div className="px-4 py-2 bg-primary/5 border-b border-primary/20">
              <div className="flex items-center gap-2 text-xs text-primary">
                <Lightbulb className="w-3 h-3" />
                <span className="font-semibold uppercase tracking-wider">Original Spark</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1 font-mono">
                {originalIdeas[0]}
              </p>
            </div>
          )}

          {/* Viewing Old Version Banner */}
          {isViewingOldVersion && (
            <div className="px-4 py-3 bg-amber-500/10 border-b border-amber-500/30 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-amber-500" />
                <span className="text-sm text-amber-500 font-medium">
                  Viewing Version {viewingVersion.index} — {new Date(viewingVersion.saved_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleBackToCurrentVersion} className="gap-1 text-xs">
                  <ChevronLeft className="w-3 h-3" />
                  Back to Current
                </Button>
                <Button size="sm" onClick={handleRestoreVersion} className="gap-1 text-xs bg-amber-500 hover:bg-amber-600 text-white">
                  <RotateCcw className="w-3 h-3" />
                  Restore This Version
                </Button>
              </div>
            </div>
          )}

          {/* Editor Content */}
          <div className="flex-1 overflow-hidden relative">
            {draftMode === "original" && hasOriginalSnapshot ? (
              /* ========== ORIGINAL VIEW (Read-only snapshot) ========== */
              <ScrollArea className="h-full">
                <div className="p-4">
                  <div className="mb-4 p-3 bg-muted/30 rounded-sm border border-border">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                      <Archive className="w-3 h-3" />
                      <span className="font-semibold uppercase tracking-wider">Original Draft (Read-only)</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      This is how your bit looked before you started structuring it into sections.
                    </p>
                  </div>
                  <div 
                    className="font-mono text-base leading-relaxed whitespace-pre-wrap bg-muted/10 p-4 rounded-sm"
                    dangerouslySetInnerHTML={{ __html: originalSnapshot }}
                  />
                </div>
              </ScrollArea>
            ) : viewMode === "script" ? (
              /* ========== SCRIPT VIEW (Full script - read-only, printable) ========== */
              <ScrollArea className="h-full">
                <div className="p-6 max-w-3xl mx-auto">
                  {/* Script Title */}
                  <h1 className="text-2xl font-semibold text-center mb-8 font-serif">
                    {title || 'Untitled Bit'}
                  </h1>
                  
                  {/* Full Script Content */}
                  <div className="font-mono text-sm leading-relaxed whitespace-pre-wrap">
                    {sections
                      .filter(s => s.included !== false)
                      .sort((a, b) => a.order - b.order)
                      .map(s => s.content)
                      .join('\n\n')
                    }
                  </div>
                  
                  {/* Empty state */}
                  {sections.filter(s => s.included !== false).length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <EyeOff className="w-8 h-8 mx-auto mb-3 opacity-50" />
                      <p>No content to display.</p>
                      <p className="text-xs mt-1">Switch to Outline view to add sections.</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            ) : (
              /* ========== OUTLINE VIEW (Structure-focused) ========== */
              <ScrollArea className="h-full">
                <div className="p-4 space-y-2">
                  {/* Bit title header */}
                  <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-sm border border-primary/20 mb-4">
                    <Layers className="w-4 h-4 text-primary" />
                    <span className="font-semibold text-sm">
                      {title || 'Untitled Bit'}
                    </span>
                    <span className="text-xs text-muted-foreground ml-auto">
                      {isPostSectionState ? `${sections.length} section${sections.length !== 1 ? 's' : ''}` : 'Unstructured'}
                    </span>
                  </div>

                  {isPostSectionState ? (
                    /* Post-section: Ordered sections with dnd-kit */
                    <>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleSectionDragEnd}
                      >
                        <SortableContext
                          items={sections.sort((a, b) => a.order - b.order).map(s => s.section_id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-2">
                            {sections.sort((a, b) => a.order - b.order).map((section, idx) => (
                              <SortableSectionCard
                                key={section.section_id}
                                section={section}
                                idx={idx}
                                isViewingOldVersion={isViewingOldVersion}
                                highlightedSectionId={highlightedSectionId}
                                collapsedSections={collapsedSections}
                                editingSectionId={editingSectionId}
                                setEditingSectionId={setEditingSectionId}
                                handleToggleSectionIncluded={handleToggleSectionIncluded}
                                handleToggleSectionCollapse={handleToggleSectionCollapse}
                                handleUpdateSection={handleUpdateSection}
                                handleOpenSectionRemix={handleOpenSectionRemix}
                                handleDuplicateSection={handleDuplicateSection}
                                handlePromoteSectionToBit={handlePromoteSectionToBit}
                                handleDeleteSection={handleDeleteSection}
                                handleTextareaSelect={handleTextareaSelect}
                                handleSectionFocus={handleSectionFocus}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>

                      {/* Add section button */}
                      {!isViewingOldVersion && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAddSection}
                          className="w-full gap-2 border-dashed mt-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add Section
                        </Button>
                      )}
                    </>
                  ) : (
                    /* Pre-section: Show body content with extraction hint */
                    <div className="border border-border rounded-sm bg-card">
                      <div className="p-3 border-b border-border bg-muted/30">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Body Content (Select text to extract sections)
                        </span>
                      </div>
                      <div className="p-3">
                        <SimpleEditor
                          value={content}
                          onChange={isViewingOldVersion ? () => {} : setContent}
                          onSelectionChange={(info) => handleSelectionChange(info, null)}
                          placeholder="Start writing your bit..."
                          readOnly={isViewingOldVersion}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
            
            {/* Selection Actions Popup */}
            {showSelectionActions && selectionInfo && !isViewingOldVersion && draftMode === "draft" && (
              <div className="absolute top-4 right-4 bg-card border border-border rounded-sm shadow-lg p-2 flex flex-col gap-1 z-10">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleExtractAsSection}
                  className="gap-2 text-xs justify-start"
                >
                  <Layers className="w-3 h-3" />
                  Extract as Section
                </Button>
                
                <div className="h-px bg-border my-1" />
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAddNote}
                  className="gap-2 text-xs justify-start"
                >
                  <StickyNote className="w-3 h-3" />
                  Add Note
                </Button>
                
                {onPromoteToBit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handlePromoteTobit}
                    className="gap-2 text-xs justify-start text-muted-foreground"
                  >
                    <FileUp className="w-3 h-3" />
                    Promote to Bit
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Version History */}
          {bit && versions.length > 0 && !isViewingOldVersion && (
            <div className="flex-shrink-0 px-4 py-3 border-t border-border bg-muted/30">
              <Collapsible>
                <CollapsibleTrigger className="flex items-center gap-2 w-full text-left">
                  <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform [[data-state=open]_&]:rotate-90" />
                  <History className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Version History ({versions.length})
                  </span>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3">
                  <ScrollArea className="h-32 border border-border rounded-sm bg-background/50">
                    <div className="p-2 space-y-1">
                      {versions.map((version, index) => (
                        <button 
                          key={version.version_id}
                          type="button"
                          onClick={() => handleViewVersion(version, index)}
                          className="w-full flex items-center justify-between p-2 hover:bg-muted/50 rounded-sm text-left transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-semibold text-muted-foreground">
                              v{versions.length - index}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(version.saved_at).toLocaleDateString()} at {new Date(version.saved_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}
        </main>
      </div>
      
      {/* Add Note Dialog */}
      <Dialog open={isNoteDialogOpen} onOpenChange={setIsNoteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="w-4 h-4" />
              Add Note
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                Selected Text
              </p>
              <p className="text-sm bg-muted/50 p-3 rounded-sm font-mono italic">
                &ldquo;{noteSelection}&rdquo;
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                Your Note
              </p>
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add a craft reminder, intent note, or observation..."
                className="min-h-[100px]"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNoteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveNote} disabled={!noteText.trim()}>
              Save Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Remix Menu Modal */}
      <RemixMenu
        isOpen={isRemixOpen}
        onClose={() => {
          setIsRemixOpen(false);
          setRemixTargetSection(null);
        }}
        originalContent={remixContent}
        targetLabel={remixTargetSection ? remixTargetSection.title || 'Section' : title || 'Bit'}
        isSection={!!remixTargetSection}
        onApplyDraft={(draftText) => {
          // Append draft to the target section or main content
          if (remixTargetSection) {
            // Append to section
            const updatedContent = (remixTargetSection.content || '') + '\n\n' + draftText;
            handleUpdateSection(remixTargetSection.section_id, { content: updatedContent });
          } else if (isPostSectionState) {
            // If we're in post-section state but remixing the whole bit,
            // append to the last section
            if (sections.length > 0) {
              const lastSection = sections[sections.length - 1];
              const updatedContent = (lastSection.content || '') + '\n\n' + draftText;
              handleUpdateSection(lastSection.section_id, { content: updatedContent });
            }
          } else {
            // Pre-section state: append to main content
            setContent((prev) => (prev || '') + '\n\n' + draftText);
          }
        }}
      />
    </div>
  );
};

export default BitWorkshop;
